import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AdminPanelSettingsOutlined,
  AnalyticsOutlined,
  ArrowOutward,
  CampaignOutlined,
  ChevronRight,
  DataObjectOutlined,
  DirectionsCarOutlined,
  HubOutlined,
  InsightsOutlined,
  PaymentsOutlined,
  SecurityOutlined,
  SettingsOutlined,
  StorefrontOutlined,
  UpdateOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

const toneMap = {
  native: { label: 'Mogoo native', color: '#0F766E', background: '#ECFDF5', border: '#A7F3D0' },
  platform: { label: 'Platform control', color: '#1D4ED8', background: '#EFF6FF', border: '#BFDBFE' },
  legacy: { label: 'Read-only legacy', color: '#9A5B08', background: '#FFF7ED', border: '#FED7AA' },
};

export const PLATFORM_DESTINATIONS = [
  {
    title: '입점 매장 운영',
    description: '지점 담당자가 사용하는 매장 운영 콘솔을 점검하고, 실제 화면 흐름을 확인합니다.',
    path: '/owner-v2',
    tone: 'platform',
    icon: StorefrontOutlined,
  },
  {
    title: '관리자 · 권한',
    description: '플랫폼 관리자와 매장 운영자의 접근 범위를 관리합니다.',
    path: '/admins',
    tone: 'platform',
    icon: AdminPanelSettingsOutlined,
  },
  {
    title: '플랫폼 설정',
    description: '공통 운영 정책과 관리자 환경 설정을 관리합니다.',
    path: '/settings',
    tone: 'native',
    icon: SettingsOutlined,
  },
  {
    title: '배너 · 공지 노출',
    description: '입점 매장과 고객에게 보여 줄 운영 콘텐츠를 관리합니다.',
    path: '/banners',
    tone: 'native',
    icon: CampaignOutlined,
  },
  {
    title: '버전 관리',
    description: '앱과 운영 도구의 배포 안내 및 최신 버전 상태를 확인합니다.',
    path: '/versions',
    tone: 'native',
    icon: UpdateOutlined,
  },
  {
    title: '매출 · 정산',
    description: '플랫폼에서 수집한 매출 현황과 결제 운영 흐름을 확인합니다.',
    path: '/sales',
    tone: 'native',
    icon: PaymentsOutlined,
  },
  {
    title: '이용 통계',
    description: '고객 이용과 서비스 흐름을 전체 관점에서 분석합니다.',
    path: '/usage',
    tone: 'native',
    icon: AnalyticsOutlined,
  },
  {
    title: 'ModernWash 전주점',
    description: '전주점 운영 기준과 외주 V1·JJ001 읽기 전용 원천을 구분해 확인합니다.',
    path: '/modernwash-ops',
    tone: 'legacy',
    icon: DirectionsCarOutlined,
  },
];

const scopeItems = [
  {
    icon: HubOutlined,
    title: '테넌트 경계',
    description: '입점사와 지점은 각자의 범위 안에서만 운영 데이터를 조회합니다.',
  },
  {
    icon: SecurityOutlined,
    title: '역할 기반 접근',
    description: '플랫폼 관리자만 전체 정책과 권한을 변경할 수 있습니다.',
  },
  {
    icon: DataObjectOutlined,
    title: '데이터 원천 분리',
    description: 'Mogoo 운영 데이터와 ModernWash 레거시 기록을 분명하게 구분합니다.',
  },
];

const SurfaceTag = ({ tone }) => {
  const item = toneMap[tone];
  return (
    <Chip
      label={item.label}
      size="small"
      sx={{
        color: item.color,
        bgcolor: item.background,
        border: `1px solid ${item.border}`,
        fontWeight: 800,
      }}
    />
  );
};

const DestinationCard = ({ destination }) => {
  const Icon = destination.icon;
  return (
    <Paper
      component={RouterLink}
      to={destination.path}
      aria-label={`${destination.title} 열기`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 202,
        p: { xs: 2, sm: 2.25 },
        color: 'inherit',
        textDecoration: 'none',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
          borderColor: '#A9B9D6',
          boxShadow: '0 12px 26px rgba(30, 41, 59, 0.10)',
          transform: 'translateY(-2px)',
        },
        '&:focus-visible': { outline: '3px solid #93C5FD', outlineOffset: 3 },
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box
          aria-hidden="true"
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 40,
            height: 40,
            borderRadius: 2.5,
            color: '#155EEF',
            bgcolor: '#EFF6FF',
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <SurfaceTag tone={destination.tone} />
      </Stack>
      <Typography component="h3" variant="subtitle1" sx={{ mt: 2.25, fontWeight: 800, fontSize: 16 }}>
        {destination.title}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary', lineHeight: 1.6 }}>
        {destination.description}
      </Typography>
      <Box sx={{ mt: 'auto', pt: 2, display: 'flex', alignItems: 'center', color: '#155EEF', gap: 0.35 }}>
        <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 800 }}>
          관리 화면 열기
        </Typography>
        <ChevronRight fontSize="small" />
      </Box>
    </Paper>
  );
};

/**
 * Mogoo master-only control-plane landing page.
 * Access enforcement deliberately stays at the routing layer so this component remains presentational.
 */
export default function PlatformAdminPortal() {
  return (
    <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, sm: 3.5 }, py: { xs: 3, md: 4.5 } }}>
      <Stack spacing={{ xs: 3, md: 4 }}>
        <Box component="header">
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
            <Box
              aria-hidden="true"
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 30,
                height: 30,
                borderRadius: 2,
                color: '#FFFFFF',
                bgcolor: '#155EEF',
                fontWeight: 900,
                fontSize: 13,
              }}
            >
              M
            </Box>
            <Typography variant="subtitle2" sx={{ color: '#344054', fontWeight: 800 }}>
              Mogoo platform
            </Typography>
            <Chip label="Master only" size="small" sx={{ bgcolor: '#F2F4F7', color: '#475467', fontWeight: 800 }} />
          </Stack>
          <Typography component="h1" variant="h3" sx={{ fontWeight: 850, letterSpacing: '-0.04em', fontSize: { xs: 30, sm: 38 } }}>
            모구 플랫폼 운영 센터
          </Typography>
          <Typography variant="body1" sx={{ mt: 1.25, maxWidth: 680, color: 'text.secondary', lineHeight: 1.65 }}>
            입점 매장, 운영 권한, 공통 정책과 서비스 상태를 플랫폼 관점에서 관리합니다.
            매장 단위 운영은 점주 콘솔에서, ModernWash V1 이력은 읽기 전용 화면에서 분리해 확인합니다.
          </Typography>
        </Box>

        <Paper
          sx={{
            overflow: 'hidden',
            borderColor: '#B9CCF9',
            background: 'linear-gradient(112deg, #F0F6FF 0%, #F9FBFF 58%, #FFFFFF 100%)',
          }}
        >
          <Box sx={{ p: { xs: 2.25, sm: 3, md: 3.5 }, display: 'grid', gap: 3, gridTemplateColumns: { md: '1fr auto' } }}>
            <Box>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <InsightsOutlined sx={{ color: '#155EEF', fontSize: 20 }} />
                <Typography variant="overline" sx={{ color: '#155EEF', fontWeight: 900, letterSpacing: '0.08em' }}>
                  CONTROL PLANE
                </Typography>
              </Stack>
              <Typography component="h2" variant="h5" sx={{ mt: 1, fontWeight: 850 }}>
                공통 정책은 한 곳에서, 현장 운영은 각 매장에서
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, maxWidth: 680, color: 'text.secondary', lineHeight: 1.65 }}>
                이 화면은 전체 Mogoo를 위한 관리 관문입니다. 입점 매장 운영을 직접 대체하지 않으며,
                서비스별 데이터 원천과 변경 권한을 명확하게 유지합니다.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'row', md: 'column' }} spacing={1} sx={{ justifyContent: 'center', minWidth: { md: 182 } }}>
              <Button component={RouterLink} to="/owner-v2" variant="contained" endIcon={<ArrowOutward />}>
                매장 운영 보기
              </Button>
              <Button component={RouterLink} to="/admins" variant="outlined" endIcon={<ArrowOutward />}>
                권한 관리
              </Button>
            </Stack>
          </Box>
          <Divider />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
            {[
              ['Mogoo native', '신규 운영 · 권한 · 정책의 기준 데이터'],
              ['Store scoped', '점주 콘솔은 매장 범위 안에서만 조회'],
              ['Legacy read-only', 'ModernWash V1 과거 기록은 수정 불가'],
            ].map(([label, description], index) => (
              <Box
                key={label}
                sx={{
                  p: 2,
                  borderLeft: { sm: index ? '1px solid #D7E3FC' : 'none' },
                  borderTop: { xs: index ? '1px solid #D7E3FC' : 'none', sm: 'none' },
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', color: '#155EEF', fontWeight: 900 }}>
                  {label}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.35, color: 'text.secondary' }}>
                  {description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        <Box component="section" aria-labelledby="platform-destinations">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography id="platform-destinations" component="h2" variant="h6" sx={{ fontSize: 18, fontWeight: 850 }}>
                플랫폼 관리 도구
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.35, color: 'text.secondary' }}>
                목적이 다른 콘솔과 데이터를 구분해 안전하게 관리합니다.
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: { sm: 'center' } }}>
              8개 관리 영역
            </Typography>
          </Stack>
          <Box sx={{ display: 'grid', gap: 1.75, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' } }}>
            {PLATFORM_DESTINATIONS.map((destination) => <DestinationCard key={destination.path} destination={destination} />)}
          </Box>
        </Box>

        <Box component="section" aria-labelledby="governance-title">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { md: 'stretch' } }}>
            <Paper sx={{ flex: '1 1 0', p: { xs: 2.25, sm: 3 } }}>
              <Typography id="governance-title" component="h2" variant="h6" sx={{ fontSize: 18, fontWeight: 850 }}>
                운영 경계 원칙
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary' }}>
                플랫폼 확장 전에도 이 세 가지 경계는 유지되어야 합니다.
              </Typography>
              <Stack spacing={2} sx={{ mt: 2.5 }}>
                {scopeItems.map(({ icon: Icon, title, description }) => (
                  <Stack key={title} direction="row" spacing={1.25} sx={{ alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'grid', placeItems: 'center', flex: '0 0 auto', width: 30, height: 30, borderRadius: 2, bgcolor: '#F2F4F7', color: '#344054' }}>
                      <Icon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{title}</Typography>
                      <Typography variant="body2" sx={{ mt: 0.2, color: 'text.secondary', lineHeight: 1.55 }}>{description}</Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Paper>
            <Alert
              severity="warning"
              variant="outlined"
              icon={<DirectionsCarOutlined fontSize="inherit" />}
              sx={{ flex: '1 1 0', alignItems: 'flex-start', p: { xs: 2, sm: 2.5 }, bgcolor: '#FFFCF5', borderColor: '#FED7AA' }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 850, color: '#7A3E00' }}>
                ModernWash 전주점 데이터 안내
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, color: '#7A3E00', lineHeight: 1.6 }}>
                외주 V1에서 받은 회원·차량·구독·세차·결제 이력은 민원과 운영 확인을 위한 레거시 참조 데이터입니다.
                Mogoo의 신규 계약, 결제, 권한 및 세차 승인 기준과 혼용하지 않습니다.
              </Typography>
              <Button component={RouterLink} to="/modernwash" size="small" sx={{ mt: 1.5, color: '#9A5B08' }} endIcon={<ArrowOutward />}>
                읽기 전용 이력 확인
              </Button>
            </Alert>
          </Stack>
        </Box>

        <Typography component="p" variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', pb: 1 }}>
          Mogoo 플랫폼 관리자 · 변경 작업은 역할과 테넌트 범위를 확인한 뒤 진행하세요.
        </Typography>
      </Stack>
    </Box>
  );
}
